import React, { useState } from 'react';
import { useQuery } from '@common/hooks';
import {
  ChangeRequestDialogBox,
  OfferChangeRequestActions,
  OfferInfo,
} from '@applicant-flow/components';
import { OFFER_CONFIRMATION_QUERY } from '@applicant-flow/graphql';
import {
  CommentBlock,
  CommentHeader,
  Drawer,
  ErrorHandler,
  LoadingState,
  ShowComments,
} from '@common/components';
import { CommentInfoFragmentType } from '@common/graphql';
import { OfferChangeResponseDrawer } from 'works/components/offer-change-response-drawer';

export function OfferPreviewDrawer({
  offerId,
  offerChangeRequestId,
  open,
  onClose,
}: {
  offerId?: string;
  offerChangeRequestId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const [showChangeResponseDrawer, setShowChangeResponseDrawer] =
    useState(false);
  const [editOffer, setEditOffer] = useState(false);

  const { data, error, loading } = useQuery(OFFER_CONFIRMATION_QUERY, {
    variables: { id: offerId || '' },
    skip: !offerId,
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="OfferPreviewDrawerNew"
        messageType="contextual"
      />
    );
  }

  const offer = data?.offer;
  const application = offer?.staffingRequestApplication;
  const isDataLoaded = !!(offer && application);
  const changeRequests = offer?.offerChangeRequests || [];
  const lastChangeRequest = changeRequests?.[changeRequests?.length - 1];

  return (
    <>
      <Drawer
        title="Offer Letter"
        className="show-offer-modal"
        open={open}
        onClose={onClose}
        footer={
          isDataLoaded && (
            <OfferChangeRequestActions
              offerState={offer?.state}
              offerChangeRequests={offer?.offerChangeRequests}
              onChangeResponse={(edit) => {
                setShowChangeResponseDrawer(true);
                setEditOffer(edit);
              }}
            />
          )
        }
      >
        {isDataLoaded ? (
          <OfferInfo
            application={application}
            offer={offer}
            footerSlot={
              offer?.comments?.length ? (
                <ShowComments
                  showMode="all"
                  commentable={offer}
                  headerSlot={<CommentHeader text="Offer Comments" />}
                  render={(comment: CommentInfoFragmentType) => (
                    <CommentBlock key={comment.id} comment={comment} />
                  )}
                />
              ) : null
            }
            headerSlot={
              lastChangeRequest && (
                <ChangeRequestDialogBox changeRequest={lastChangeRequest} />
              )
            }
          />
        ) : loading ? (
          <LoadingState />
        ) : null}
      </Drawer>

      {isDataLoaded && (
        <OfferChangeResponseDrawer
          onDrawerClose={() => setShowChangeResponseDrawer(false)}
          offerChangeRequestId={offerChangeRequestId}
          visible={showChangeResponseDrawer}
          application={application}
          offer={offer}
          edit={editOffer}
        />
      )}
    </>
  );
}
